// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * GameLobby - Reusable lobby component for game boards.
 *
 * Provides a consistent lobby UI with:
 * - Game title and emoji
 * - Room code display
 * - How to play instructions
 * - Player list (via Scoreboard)
 * - Start game button (for host)
 * - Waiting message (when not enough players)
 */

import { Scoreboard } from '../../Scoreboard';
import type { PlayerInfo } from '../../../types/game';

export interface GameLobbyProps {
  /** Game title (e.g., "FauxFacts", "Liar's Hand") */
  gameTitle: string;
  /** Game emoji */
  gameEmoji: string;
  /** Game description */
  description: string;
  /** Room code for display */
  roomCode: string;
  /** List of players in the room */
  players: PlayerInfo[];
  /** Current player's ID */
  currentPlayerId: string;
  /** Whether current player is host */
  isHost: boolean;
  /** Minimum players required to start */
  minPlayers: number;
  /** Whether start action is in progress */
  isStarting?: boolean;
  /** Handler for start game button */
  onStartGame: () => void;
  /** Optional "How to Play" instructions (array of steps) */
  howToPlay?: string[];
  /** Optional custom content to render between description and players */
  children?: React.ReactNode;
  /** Data testid prefix (e.g., "faux_facts" -> "faux_facts-start-game-button") */
  testIdPrefix?: string;
}

/**
 * Standard lobby component for game boards.
 *
 * @example
 * ```tsx
 * <GameLobby
 *   gameTitle="FauxFacts"
 *   gameEmoji="🦬"
 *   description="Submit fake answers and fool your friends!"
 *   roomCode={roomCode}
 *   players={players}
 *   currentPlayerId={playerId}
 *   isHost={isHost}
 *   minPlayers={3}
 *   isStarting={isStarting}
 *   onStartGame={handleStartGame}
 *   howToPlay={[
 *     "Everyone sees a trivia question",
 *     "Submit a fake answer",
 *     "Vote on which is real",
 *     "Earn points!",
 *   ]}
 *   testIdPrefix="faux_facts"
 * />
 * ```
 */
export function GameLobby({
  gameTitle,
  gameEmoji,
  description,
  roomCode: _roomCode,
  players,
  currentPlayerId,
  isHost,
  minPlayers,
  isStarting = false,
  onStartGame,
  howToPlay,
  children,
  testIdPrefix,
}: GameLobbyProps) {
  const canStart = players.length >= minPlayers;
  const testId = testIdPrefix ? `${testIdPrefix}-start-game-button` : 'game-lobby-start-button';

  return (
    <div className="max-w-4xl mx-auto" data-testid="game-lobby-container">
      <div
        className="rounded-xl shadow-lg p-8 text-center"
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div className="text-6xl mb-4">{gameEmoji}</div>
        <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          {gameTitle}
        </h2>
        <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
          {description}
        </p>

        {howToPlay && howToPlay.length > 0 && (
          <div
            className="rounded-lg p-4 mb-6 text-left"
            style={{
              backgroundColor: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border)',
            }}
          >
            <h3 className="font-bold mb-2" style={{ color: 'var(--color-accent)' }}>
              How to Play:
            </h3>
            <ol
              className="list-decimal list-inside space-y-1 text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {howToPlay.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {children}

        {players.length > 0 && (
          <div className="mt-6" data-testid="game-lobby-player-list">
            <Scoreboard
              players={players}
              currentPlayerId={currentPlayerId}
              playerActionStatuses={{}}
            />
          </div>
        )}

        {isHost && canStart && (
          <button
            onClick={onStartGame}
            disabled={isStarting}
            className="mt-4 px-6 py-3 rounded-lg disabled:opacity-50 font-bold"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-surface)',
            }}
            type="button"
            data-testid={testId}
          >
            Start Game
          </button>
        )}

        {isHost && !canStart && (
          <p
            className="mt-4"
            style={{ color: 'var(--color-text-muted)' }}
            data-testid="game-lobby-waiting-message"
          >
            Need at least {minPlayers} players to start
          </p>
        )}

        {!isHost && (
          <p className="mt-4" style={{ color: 'var(--color-text-muted)' }}>
            Waiting for host to start the game...
          </p>
        )}
      </div>
    </div>
  );
}
